// eslint-disable-next-line import/no-unresolved
import { Auth3dsParams, Auth3dsResult } from 'yl-sony-3ds/YlSony3ds';
import { flow, Instance, types } from 'mobx-state-tree';

import { PaymentMethodProviderType } from '../../../../../../external/shared/api/EnrollmentClient.generated';
import { getRootStore } from '../../../../../root/RootStoreUtils';

export const Sony3dsStore = types
  .model({})
  .volatile(() => ({
    initPromise: undefined as Promise<typeof import('yl-sony-3ds/YlSony3ds')> | undefined
  }))
  .views(self => ({
    get isEnabled(): boolean {
      const { config, isProviderEnabled } = getRootStore(self).moduleStores.threeDSecureEntry!;
      return config.sony3dsEnabled && isProviderEnabled(PaymentMethodProviderType.Sony, false);
    }
  }))
  .actions(self => ({
    prepareAuthParams(): Auth3dsParams {
      const rootStore = getRootStore(self);
      const { billingEntry, appliedPaymentsReview, shippingAndBillingReview, orderSummary, personalInfoReview } =
        rootStore.moduleStores;

      const { currency } = rootStore.reference;

      const creditCardData = billingEntry?.creditCardData ?? appliedPaymentsReview!.creditCardData;
      const billingAddress = billingEntry?.addressForBilling ?? shippingAndBillingReview!.billingAddress;
      const shippingAddress = billingEntry?.userShippingAddress ?? shippingAndBillingReview!.userShippingAddress;

      return {
        currencyCode: currency.code,
        paymentMethodId: creditCardData.identity!,
        returnUrl: rootStore.moduleStores.threeDSecureEntry!.redirectReturnUrl,
        customer: {
          email: personalInfoReview!.email,
          phoneNumber: personalInfoReview!.primaryPhone,
          firstName: personalInfoReview!.firstName,
          lastName: personalInfoReview!.lastName
        },
        billingAddress: {
          recipient: billingAddress.recipient!,
          phoneNumber: billingAddress.phoneNumber!,
          street1: billingAddress.street1!,
          street2: billingAddress.street2!,
          street3: billingAddress.street3,
          city: billingAddress.city,
          stateProv: billingAddress.stateProv!,
          postalCode: billingAddress.postalCode!,
          countryIsoCode2: billingAddress.country!
        },
        shippingAddress: {
          recipient: shippingAddress.recipient!,
          phoneNumber: shippingAddress.phoneNumber!,
          street1: shippingAddress.street1!,
          street2: shippingAddress.street2!,
          street3: shippingAddress.street3,
          city: shippingAddress.city,
          stateProv: shippingAddress.stateProv!,
          postalCode: shippingAddress.postalCode!,
          countryIsoCode2: shippingAddress.country!
        },
        creditCard: {
          expMonth: creditCardData.expirationMonth!,
          expYear: creditCardData.expirationYear!,
          nameOnCard: creditCardData.name!,
          tokenExToken: creditCardData.tokenExToken!
        },
        order: {
          amount: currency.toDecimalAmount(appliedPaymentsReview!.amount),
          externalReferenceId: orderSummary!.externalReferenceId,
          isSubscription: orderSummary!.isErEnrollment
        }
      };
    }
  }))
  .actions(self => ({
    async initAuth(): Promise<void> {
      // eslint-disable-next-line import/no-unresolved
      self.initPromise ??= import('yl-sony-3ds/YlSony3ds');
      const { auth3dsWarmUp } = await self.initPromise;
      await auth3dsWarmUp(self.prepareAuthParams());
    }
  }))
  .actions(self => ({
    auth: flow(function* auth(): any {
      yield self.initAuth();
      const { auth3ds }: typeof import('yl-sony-3ds/YlSony3ds') = yield self.initPromise!;

      const result: Auth3dsResult = yield auth3ds(self.prepareAuthParams());
      if (!result.success) {
        throw new Error(`Unsuccessful Sony 3DS Auth: ${result.clientJsonValue}`);
      }
      return result.clientJsonValue;
    })
  }));

export type Sony3dsStore = Instance<typeof Sony3dsStore>;
